/**
 * Percent positions (0–100) for starting XI on a vertical pitch (goal at top).
 * Rows follow formation string segments, e.g. 4-2-3-1 → GK + 4 + 2 + 3 + 1.
 * X/Y ranges are widened so players sit farther apart on the tactical board.
 */

export interface LineupSlotPct {
  x: number;
  y: number;
}

const FALLBACK_4231: LineupSlotPct[] = [
  { x: 50, y: 5 },
  { x: 10, y: 23 }, { x: 36, y: 23 }, { x: 64, y: 23 }, { x: 90, y: 23 },
  { x: 41, y: 45 }, { x: 59, y: 45 },
  { x: 18, y: 67 }, { x: 50, y: 67 }, { x: 82, y: 67 },
  { x: 50, y: 88 },
];

/** Horizontal % for the i-th player in a row of `count` (0-based). */
function rowSlotX(count: number, i: number): number {
  if (count <= 0) return 50;
  if (count === 1) return 50;
  /** Double pivot / central pair: keep tight to the spine (e.g. 4-2-3-1). */
  if (count === 2) {
    return i === 0 ? 41 : 59;
  }
  return 10 + (i * (80 / (count - 1)));
}

export function getLineupFormationSlots(formation: string): LineupSlotPct[] {
  const parts = formation
    .split('-')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !Number.isNaN(n));
  const sum = parts.reduce((a, b) => a + b, 0);
  if (parts.length < 2 || sum !== 10) {
    return FALLBACK_4231;
  }
  const rowCount = parts.length;
  /** First outfield row — keep well below GK (~5) so the back line isn’t on top of the keeper. */
  const yStart = 23;
  const yEnd = 88;
  const yStep = rowCount > 1 ? (yEnd - yStart) / (rowCount - 1) : 0;
  const out: LineupSlotPct[] = [{ x: 50, y: 5 }];
  for (let r = 0; r < rowCount; r++) {
    const count = parts[r];
    const y = yStart + r * yStep;
    if (count <= 0) continue;
    for (let i = 0; i < count; i++) {
      out.push({ x: rowSlotX(count, i), y });
    }
  }
  return out;
}

/**
 * Map template Y (goal at top of a half-pitch) into full-pitch % for the top half (home).
 */
export function slotYToPitchTopHalf(slotY: number): number {
  return 2.5 + (slotY / 100) * 46.5;
}

/**
 * Map template Y into full-pitch % for the bottom half (away, goal at bottom).
 */
export function slotYToPitchBottomHalf(slotY: number): number {
  return 100 - (2.5 + (slotY / 100) * 46.5);
}
